import React from "react";
import { Link, NavLink, useNavigate } from "react-router-dom";
import { Home, LogOut, User, Menu, X } from "lucide-react";
import { Button } from "@/components/ui/button";
import { useAuth } from "@/context/AuthContext";
import api from "@/lib/api";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";

const NAV_LINKS_PUBLIC = [
  { to: "/", label: "Home" },
  { to: "/announcements", label: "Announcements" },
  { to: "/amenities", label: "Amenities" },
  { to: "/documents", label: "Documents" },
  { to: "/board", label: "Board" },
  { to: "/contact", label: "Contact" },
];

export const Navbar = () => {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const [open, setOpen] = React.useState(false);
  const [unreadCount, setUnreadCount] = React.useState(0);

  // Admins only — poll the unread board-message count every 60s so the header badge stays fresh
  // without a heavy fetch of the full message list.
  React.useEffect(() => {
    if (user?.role !== "admin") { setUnreadCount(0); return; }
    let cancelled = false;
    const fetchCount = async () => {
      try {
        const { data } = await api.get("/contact/messages/unread-count");
        if (!cancelled) setUnreadCount(Number(data?.count || 0));
      } catch { /* silent — header badge is best-effort */ }
    };
    fetchCount();
    const iv = setInterval(fetchCount, 60000);
    return () => { cancelled = true; clearInterval(iv); };
  }, [user?.role, user?.id]);

  const navLinks = React.useMemo(() => {
    const base = [{ to: "/", label: "Home" }];
    if (user) base.push({ to: "/portal", label: "Member Portal" });
    return base.concat([
      { to: "/announcements", label: "Announcements" },
      { to: "/amenities", label: "Amenities" },
      { to: "/documents", label: "Documents" },
      { to: "/board", label: "Board" },
      { to: "/get-involved", label: "Get Involved" },
      { to: "/contact", label: "Contact" },
    ]);
  }, [user]);

  const handleLogout = async () => {
    await logout();
    navigate("/");
  };

  return (
    <header
      data-testid="site-navbar"
      className="sticky top-0 z-40 border-b border-[#D5CFC4] bg-[#F9F8F6]/90 backdrop-blur-md"
    >
      <div className="max-w-7xl mx-auto px-6 py-4 flex items-center justify-between">
        <Link to="/" className="flex items-center gap-3" data-testid="navbar-logo-link">
          <div className="w-10 h-10 rounded-full bg-[#2C4C3B] flex items-center justify-center">
            <Home className="w-5 h-5 text-white" />
          </div>
          <div className="leading-tight">
            <p className="font-serif text-lg text-[#1A2421] tracking-tight">Saddleridge · Savoy Chase · The Trails</p>
            <p className="text-xs text-[#5C6A64]">Homeowners Association</p>
          </div>
        </Link>

        <nav className="hidden lg:flex items-center gap-8">
          {navLinks.map((l) => (
            <NavLink
              key={l.to}
              to={l.to}
              end={l.to === "/"}
              data-testid={`nav-link-${l.label.toLowerCase().replace(/\s+/g,'-')}`}
              className={({ isActive }) =>
                `text-sm font-medium transition-colors ${
                  isActive ? "text-[#2C4C3B]" : "text-[#5C6A64] hover:text-[#2C4C3B]"
                }`
              }
            >
              {l.label}
            </NavLink>
          ))}
        </nav>

        <div className="hidden lg:flex items-center gap-3">
          {user ? (
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button
                  data-testid="navbar-user-menu"
                  variant="outline"
                  className="rounded-full border-[#D5CFC4] bg-white"
                >
                  <User className="w-4 h-4 mr-2" /> {user.name?.split(" ")[0] || "Member"}
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end" className="w-56">
                <DropdownMenuLabel>{user.email}</DropdownMenuLabel>
                <DropdownMenuSeparator />
                <DropdownMenuItem asChild>
                  <Link to="/portal" data-testid="menu-portal-link">Member Portal</Link>
                </DropdownMenuItem>
                <DropdownMenuItem asChild>
                  <Link to="/account" data-testid="menu-account-link">My Account</Link>
                </DropdownMenuItem>
                <DropdownMenuItem asChild>
                  <Link to="/portal/pay" data-testid="menu-pay-link">Pay Dues</Link>
                </DropdownMenuItem>
                <DropdownMenuItem asChild>
                  <Link to="/portal/bookings" data-testid="menu-bookings-link">My Bookings</Link>
                </DropdownMenuItem>
                {user.role === "admin" && (
                  <DropdownMenuItem asChild>
                    <Link to="/admin" data-testid="menu-admin-link" className="flex items-center justify-between w-full">
                      <span>Admin</span>
                      {unreadCount > 0 && (
                        <span data-testid="menu-admin-unread-badge" className="ml-2 inline-flex items-center justify-center min-w-[20px] h-5 px-1.5 rounded-full bg-[#C86650] text-white text-[11px] font-semibold">
                          {unreadCount}
                        </span>
                      )}
                    </Link>
                  </DropdownMenuItem>
                )}
                <DropdownMenuSeparator />
                <DropdownMenuItem onClick={handleLogout} data-testid="menu-logout-btn">
                  <LogOut className="w-4 h-4 mr-2" /> Sign out
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
          ) : (
            <>
              <Link to="/login">
                <Button
                  data-testid="navbar-login-btn"
                  variant="ghost"
                  className="rounded-full text-[#2C4C3B] hover:bg-[#EADCC8]"
                >
                  Sign in
                </Button>
              </Link>
              <Link to="/register">
                <Button
                  data-testid="navbar-register-btn"
                  className="rounded-full bg-[#2C4C3B] hover:bg-[#233D2F] text-white"
                >
                  Member Portal
                </Button>
              </Link>
            </>
          )}
        </div>

        <button
          className="lg:hidden"
          onClick={() => setOpen(!open)}
          data-testid="navbar-mobile-toggle"
          aria-label="Toggle menu"
        >
          {open ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
        </button>
      </div>

      {open && (
        <div className="lg:hidden border-t border-[#D5CFC4] bg-white px-6 py-4 space-y-3">
          {navLinks.map((l) => (
            <Link
              key={l.to}
              to={l.to}
              onClick={() => setOpen(false)}
              className="block text-sm font-medium text-[#1A2421]"
              data-testid={`mobile-nav-${l.label.toLowerCase().replace(/\s+/g,'-')}`}
            >
              {l.label}
            </Link>
          ))}
          <div className="pt-3 border-t border-[#D5CFC4] flex flex-col gap-2">
            {user ? (
              <>
                {user.role === "admin" && (
                  <Link to="/admin" onClick={() => setOpen(false)} data-testid="mobile-admin-link">
                    <Button className="w-full rounded-full bg-[#2C4C3B] hover:bg-[#233D2F] text-white flex items-center justify-center gap-2">
                      Admin Console
                      {unreadCount > 0 && (
                        <span data-testid="mobile-admin-unread-badge" className="inline-flex items-center justify-center min-w-[20px] h-5 px-1.5 rounded-full bg-[#C86650] text-white text-[11px] font-semibold">
                          {unreadCount}
                        </span>
                      )}
                    </Button>
                  </Link>
                )}
                <Link to="/account" onClick={() => setOpen(false)} data-testid="mobile-my-account-link">
                  <Button variant="outline" className="w-full rounded-full border-[#2C4C3B] text-[#2C4C3B]">
                    <User className="w-4 h-4 mr-2" /> My Account
                  </Button>
                </Link>
                <Button variant="outline" onClick={handleLogout} className="rounded-full">
                  Sign out
                </Button>
              </>
            ) : (
              <>
                <Link to="/login" onClick={() => setOpen(false)}>
                  <Button variant="outline" className="w-full rounded-full">Sign in</Button>
                </Link>
                <Link to="/register" onClick={() => setOpen(false)}>
                  <Button className="w-full rounded-full bg-[#2C4C3B]">Member Portal</Button>
                </Link>
              </>
            )}
          </div>
        </div>
      )}
    </header>
  );
};
